import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import api from "@/lib/api";
import { T, F, Btn, th as palette } from "@/lib/bricks";
import BricksLoader from "@/components/BricksLoader";
import { Page, PageHeader, StatusTabs, Empty } from "@/components/calm/Calm";

/**
 * Read-only month view (list on phones) of showings, closings and shoots from the
 * seat's listing files. Same source as the private ICS feed; edit on the listing file.
 * With Google Calendar connected, the seat's own Google events show alongside (read-only),
 * and a stale sync (>15 min) is refreshed when the page opens.
 *
 * Staying current (2026-10-25): the agenda says `needs_sync` when a deal date,
 * condition or agent changed after the last Google push, and the page syncs at
 * once. It refetches when the tab regains focus, and drops responses from an
 * earlier month request so a slow reply can't overwrite the month on screen.
 */
const GOOGLE_STALE_MS = 15 * 60 * 1000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const KIND_LABEL = { closing: "Closing", shoot: "Shoot", google: "Google", showing: "Showing" };
function kindTone(kind, dark) {
  return { bg: dark ? "rgba(244,241,235,0.06)" : T.paperSoft, fg: dark ? T.moonSoft : T.ink, label: KIND_LABEL[kind] || "Event", outline: kind === "google" };
}

/** Local civil date of an event (date-only values stay put; instants use device tz). */
function eventDay(e) {
  const s = String(e.starts_at || "");
  if (s.length === 10) return s;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s.slice(0, 10) : ymd(d);
}
function eventTime(e) {
  const s = String(e.starts_at || "");
  if (s.length === 10) return "";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
}

function useNarrow(bp = 720) {
  const q = `(max-width:${bp}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [q]);
  return narrow;
}

export default function CalendarPage() {
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === "dark";
  const p = palette(dark);
  const navigate = useNavigate();
  const narrow = useNarrow();
  const today = ymd(new Date());
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [google, setGoogle] = useState({ connected: false });
  const [reload, setReload] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const syncedAt = useRef(0);
  const reqSeq = useRef(0);

  const gridStart = useMemo(() => { const d = new Date(cursor); d.setDate(1 - d.getDay()); return d; }, [cursor]);
  const gridDays = useMemo(() => Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; }), [gridStart]);

  useEffect(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    setError("");
    const end = new Date(gridStart); end.setDate(end.getDate() + 42);
    api.get("/calendar/agenda", { params: { start: ymd(gridStart), end: ymd(end) } })
      .then((r) => {
        if (seq !== reqSeq.current) return; // a newer month request is in flight
        setEvents(r.data?.events || []);
        setGoogle(r.data?.google || { connected: false });
      })
      .catch(() => { if (seq === reqSeq.current) setError("Couldn't load your calendar."); })
      .finally(() => { if (seq === reqSeq.current) setLoading(false); });
  }, [gridStart, reload]);

  // Changes made on a deal in another tab show when you come back.
  useEffect(() => {
    const refresh = () => { if (document.visibilityState !== "hidden") setReload((n) => n + 1); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, []);

  const syncGoogle = () => {
    if (syncing) return;
    syncedAt.current = Date.now();
    setSyncing(true);
    api.post("/integrations/google-calendar/sync")
      .then(() => setReload((n) => n + 1))
      .catch(() => setError("Couldn't sync with Google Calendar. Try again in a minute."))
      .finally(() => setSyncing(false));
  };

  useEffect(() => {
    if (!google.connected || syncing) return;
    if (Date.now() - syncedAt.current < 30 * 1000) return; // one automatic sync per 30s
    const last = google.last_synced_at ? new Date(google.last_synced_at).getTime() : 0;
    const stale = !last || Date.now() - last >= GOOGLE_STALE_MS;
    if (!google.needs_sync && !stale) return;
    syncGoogle();
  }, [google]); // runs when the Google status changes

  const shown = events.filter((e) => filter === "all" || e.kind === filter);
  const byDay = useMemo(() => {
    const m = {};
    for (const e of shown) (m[eventDay(e)] = m[eventDay(e)] || []).push(e);
    Object.values(m).forEach((list) => list.sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at))));
    return m;
  }, [shown]);
  const monthKey = `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`;
  const monthLabel = cursor.toLocaleDateString("en-CA", { month: "long", year: "numeric" });
  const open = (e) => {
    if (e.transaction_id) navigate(`/transactions/${e.transaction_id}`);
    else if (e.html_link) window.open(e.html_link, "_blank", "noopener");
  };
  const shift = (n) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + n, 1));

  const btn = { height: 44, minWidth: 44, padding: "0 12px", border: `1px solid ${p.hairS}`, borderRadius: 2, background: p.surf, color: p.ink, fontFamily: F.display, fontSize: 13, cursor: "pointer" };

  const Chip = ({ e, compact }) => {
    const tone = kindTone(e.kind, dark);
    return (
      <button
        type="button"
        onClick={() => open(e)}
        title={`${tone.label} · ${e.title}${e.location ? ` · ${e.location}` : ""}`}
        style={{ display: "flex", alignItems: compact ? "center" : "baseline", gap: compact ? 6 : 10, width: "100%", textAlign: "left", border: 0, borderRadius: 2,
          background: compact ? tone.bg : "transparent", color: tone.fg, boxShadow: compact && tone.outline ? `inset 0 0 0 1px ${p.hairS}` : "none", padding: compact ? "3px 6px" : "2px 0", fontFamily: F.display,
          fontSize: compact ? 11.5 : 13, lineHeight: 1.3, cursor: e.transaction_id || e.html_link ? "pointer" : "default", overflow: "hidden" }}
      >
        {eventTime(e) ? <span style={{ fontFamily: F.mono, fontSize: compact ? 10 : 11.5, color: p.ink3, flexShrink: 0 }}>{eventTime(e)}</span> : null}
        {compact ? (
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title || tone.label}</span>
        ) : (
          <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <span>{e.title || tone.label}</span>
            <span style={{ fontSize: 12.5, color: p.ink3 }}>{[tone.label, e.location].filter(Boolean).join(" · ")}</span>
          </span>
        )}
      </button>
    );
  };

  const filters = [["all", "All"], ["showing", "Showings"], ["closing", "Closings"], ["shoot", "Shoots"], ...(google.connected ? [["google", "Google"]] : [])];

  return (
    <Page testid="calendar-page" width={1200}>
      <PageHeader
        title="Calendar"
        subtitle={<>Showings, closings and shoots from your listing files. Edit them on the file.
          {google.connected ? " Synced with Google Calendar." : google.needs_reconnect ? " Google Calendar needs reconnecting in Connected tools." : ""}</>}
        action={google.connected
          ? <Btn data-testid="calendar-sync" onClick={syncGoogle} disabled={syncing}>{syncing ? "Syncing…" : "Sync now"}</Btn>
          : <Btn data-testid="calendar-connect" onClick={() => navigate("/integrations")}>{google.needs_reconnect ? "Reconnect Google Calendar" : "Connect Google Calendar"}</Btn>}
      />
      <StatusTabs label="Event type" options={filters.map(([key, label]) => ({ key, label }))} value={filter} onChange={setFilter} testid="calendar-filter" hit />

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button type="button" aria-label="Previous month" style={btn} onClick={() => shift(-1)}>‹</button>
        <button type="button" aria-label="Next month" style={btn} onClick={() => shift(1)}>›</button>
        <button type="button" style={btn} onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Today</button>
        <div style={{ fontSize: 17, fontWeight: 500, marginLeft: 6 }} aria-live="polite">{monthLabel}</div>
        {loading ? <BricksLoader variant="cycle" size={18} color={T.brick} style={{ marginLeft: 8 }} /> : null}
      </div>

      {error ? <div style={{ fontSize: 13, color: T.bad }}>{error}</div> : null}

      {narrow ? (
        <div style={{ display: "flex", flexDirection: "column", borderTop: `1px solid ${p.hair}` }}>
          {(() => {
            const days = Object.keys(byDay).filter((d) => d.startsWith(monthKey)).sort();
            if (!loading && days.length === 0) {
              return <Empty>Nothing scheduled in {monthLabel}.</Empty>;
            }
            return days.map((d, i) => {
              const dt = new Date(`${d}T12:00:00`);
              return (
                <div key={d} style={{ display: "flex", gap: 14, padding: "14px 0", borderBottom: `1px solid ${p.hair}` }}>
                  <div style={{ width: 44, flexShrink: 0, textAlign: "center" }}>
                    <div style={{ fontSize: 12, color: p.ink3 }}>{WEEKDAYS[dt.getDay()]}</div>
                    <div style={{ fontSize: 22, fontWeight: 500, color: p.ink, textDecoration: d === today ? "underline" : "none", textUnderlineOffset: 4, fontVariantNumeric: "tabular-nums" }}>{dt.getDate()}</div>
                  </div>
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
                    {byDay[d].map((e) => <Chip key={e.id} e={e} />)}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      ) : (
        <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2, background: p.hair, display: "grid", gridTemplateColumns: "repeat(7, minmax(0,1fr))", gap: 1, overflow: "hidden" }}>
          {WEEKDAYS.map((w) => (
            <div key={w} style={{ background: p.surf2, padding: "8px 10px" }}><span style={{ fontSize: 12, color: p.ink3 }}>{w}</span></div>
          ))}
          {gridDays.map((d) => {
            const k = ymd(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const list = byDay[k] || [];
            const isToday = k === today;
            return (
              <div key={k} style={{ background: inMonth ? p.surf : p.bg, minHeight: 112, padding: 6, display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <span style={{ minWidth: 24, height: 24, lineHeight: "24px", textAlign: "center", borderRadius: 2, fontSize: 12.5, fontVariantNumeric: "tabular-nums",
                    background: isToday ? p.ink : "transparent", color: isToday ? p.bg : inMonth ? p.ink2 : p.ink4, fontWeight: isToday ? 600 : 400 }}>{d.getDate()}</span>
                </div>
                {list.slice(0, 3).map((e) => <Chip key={e.id} e={e} compact />)}
                {list.length > 3 ? <div style={{ fontFamily: F.mono, fontSize: 10, color: p.ink3, padding: "0 6px" }}>+{list.length - 3} more</div> : null}
              </div>
            );
          })}
        </div>
      )}
    </Page>
  );
}
